/* Dialog for editing an existing goal (update name/tags/duration or delete). */

import { useState, useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Trash2 } from "lucide-react";
import { SourceTagSelect } from "@/components/sources/SourceTagSelect";
import { GoalIconPicker } from "@/components/goals/GoalIconPicker";
import {
  GOAL_TIME_OPTIONS,
  formatMinutes,
  parseTimeInput,
  validateGoalMinutes,
} from "@/components/goals/goalTimeOptions";

export interface EditGoalData {
  id: Id<"tagGoals">;
  name: string;
  tags: string[];
  icon?: string | null;
  dailyGoalMinutes: number;
}

interface EditGoalDialogProps {
  goal: EditGoalData | null;
  onOpenChange: (open: boolean) => void;
}

export function EditGoalDialog({ goal, onOpenChange }: EditGoalDialogProps) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("\u{1F3AF}");
  const [tags, setTags] = useState<string[]>([]);
  const [timeInput, setTimeInput] = useState("");
  const [timeError, setTimeError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateGoal = useMutation(api.tagGoals.update);
  const removeGoal = useMutation(api.tagGoals.remove);
  const availableTags = useQuery(api.tags.getAvailableTags) ?? [];
  const addTag = useMutation(api.tags.addTag);

  const portalTargetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (goal) {
      setName(goal.name);
      setIcon(goal.icon ?? "\u{1F3AF}");
      setTags(goal.tags);
      setTimeInput(formatMinutes(goal.dailyGoalMinutes));
      setTimeError(null);
      setIsSubmitting(false);
      setIsDeleting(false);
      setError(null);
    }
  }, [goal]);

  const resetAndClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  function handleTimeInputChange(value: string) {
    setTimeInput(value);
    setTimeError(null);
  }

  function handlePresetClick(minutes: number) {
    setTimeInput(formatMinutes(minutes));
    setTimeError(null);
  }

  function resolveMinutes(): number | null {
    const parsed = parseTimeInput(timeInput);
    if (parsed === null) {
      setTimeError("Enter a value like 30m, 1.5h, or 90");
      return null;
    }
    const validationError = validateGoalMinutes(parsed);
    if (validationError) {
      setTimeError(validationError);
      return null;
    }
    return parsed;
  }

  async function handleCreateTag(tag: string) {
    await addTag({ tag });
  }

  async function handleSave() {
    if (!goal || !name.trim() || tags.length === 0) return;

    const minutes = resolveMinutes();
    if (minutes === null) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await updateGoal({
        id: goal.id,
        name: name.trim(),
        tags,
        icon,
        dailyGoalMinutes: minutes,
      });
      resetAndClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update goal";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!goal) return;

    setIsDeleting(true);
    setError(null);

    try {
      await removeGoal({ id: goal.id });
      resetAndClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to delete goal";
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  }

  const canSave =
    name.trim().length > 0 && tags.length > 0 && !isSubmitting && !isDeleting;
  const isBusy = isSubmitting || isDeleting;

  return (
    <Dialog open={goal !== null} onOpenChange={(open) => { if (!open) resetAndClose(); }}>
      <DialogContent className="sm:max-w-md">
        <div ref={portalTargetRef} className="absolute" />
        <DialogHeader>
          <DialogTitle>Edit Goal</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-goal-name">Name</Label>
            <div className="flex gap-2">
              <GoalIconPicker value={icon} onChange={setIcon} />
              <Input
                id="edit-goal-name"
                placeholder="e.g. Learn Spanish"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Tags</Label>
            <SourceTagSelect
              selectedTags={tags}
              onTagsChange={setTags}
              availableTags={availableTags}
              onCreateTag={handleCreateTag}
              portalContainer={portalTargetRef}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-goal-time">Duration</Label>
            <Input
              id="edit-goal-time"
              placeholder="e.g. 1h, 30m, 90"
              value={timeInput}
              onChange={(e) => handleTimeInputChange(e.target.value)}
              autoComplete="off"
            />
            <div className="flex flex-wrap gap-1.5">
              {GOAL_TIME_OPTIONS.map((opt) => (
                <button
                  key={opt.minutes}
                  type="button"
                  onClick={() => handlePresetClick(opt.minutes)}
                  className="rounded-md border px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {timeError && (
              <p className="text-destructive text-sm">{timeError}</p>
            )}
          </div>

          {error && (
            <p className="text-destructive text-sm">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isBusy}
            className="sm:mr-auto"
          >
            {isDeleting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
            Delete
          </Button>
          <Button variant="outline" onClick={resetAndClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isSubmitting && (
              <Loader2 className="size-4 animate-spin" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
